import { PUBLISHED_TOGGLED } from "@/js/addon/toggle";

/**
 * Sélection des QR codes dynamiques à télécharger en archive (« téléchargement à la
 * demande »).
 *
 * Le formulaire `form[data-link-selection]` vit dans le conteneur du filtre, rafraîchi en
 * AJAX à chaque changement : tout passe par délégation sur `document.body`, rien n'est
 * accroché aux éléments eux-mêmes. Deux façons de choisir, un seul bouton :
 *
 *  - cocher des lignes une par une (`input[name="ids[]"]`, rattachées au formulaire par
 *    l'attribut `form`) : le bouton compte les cases cochées ;
 *  - cocher « Tout » : c'est tout le filtre en cours, toutes pages confondues, pas
 *    seulement la page affichée. Le drapeau caché `all` passe à 1 et c'est le serveur qui
 *    rejoue le filtre ; le bouton affiche le total que le serveur a compté (`data-total`).
 *    Décocher une ligne pendant que « Tout » est coché revient à une sélection ligne par ligne.
 *
 * Seul un code publié se télécharge : la case d'une ligne suit donc son bouton de
 * publication, que `toggle.ts` remplace sans recharger la ligne.
 */
export default function initLinkSelection(): void {
    document.body.addEventListener(PUBLISHED_TOGGLED, e => {
        const toggle = e.target as HTMLElement;
        const form = document.querySelector<HTMLFormElement>("form[data-link-selection]");
        const row = toggle.closest("tr")?.querySelector<HTMLInputElement>(`input[name="ids[]"][form="${form?.id}"]`);
        if (!form || !row) {
            return;
        }

        row.disabled = "1" !== toggle.dataset.published;
        row.checked = !row.disabled && "1" === flag(form).value;
        refresh(form);
    });

    document.body.addEventListener("change", e => {
        const target = e.target as HTMLElement;
        const form = document.querySelector<HTMLFormElement>("form[data-link-selection]");
        if (!form) {
            return;
        }

        if (target.matches("[data-select-all]")) {
            const all = (target as HTMLInputElement).checked;
            flag(form).value = all ? "1" : "0";
            rows(form).forEach(row => {
                row.checked = all;
            });
            refresh(form);

            return;
        }

        if (target.matches(`input[name="ids[]"][form="${form.id}"]`)) {
            if ("1" === flag(form).value) {
                flag(form).value = "0";
                const all = form.querySelector<HTMLInputElement>("[data-select-all]");
                if (all) {
                    all.checked = false;
                }
            }
            refresh(form);
        }
    });
}

/** Le nombre que le bouton affiche : le total du filtre si « Tout » est coché, sinon les lignes cochées. */
export function selectedCount(form: HTMLFormElement): number {
    if ("1" === flag(form).value) {
        return parseInt(form.dataset.total ?? "0", 10) || 0;
    }

    return rows(form).filter(row => row.checked).length;
}

function refresh(form: HTMLFormElement): void {
    const count = selectedCount(form);
    const button = form.querySelector<HTMLButtonElement>("[data-submit]");
    const label = form.querySelector<HTMLElement>("[data-submit-label]");
    if (label) {
        label.textContent = (label.dataset.template ?? "%count%").replace("%count%", String(count));
    }
    if (button) {
        button.disabled = 0 === count;
    }
}

function flag(form: HTMLFormElement): HTMLInputElement {
    return form.querySelector<HTMLInputElement>("[data-all-flag]")!;
}

function rows(form: HTMLFormElement): HTMLInputElement[] {
    return Array.from(document.querySelectorAll<HTMLInputElement>(`input[name="ids[]"][form="${form.id}"]:not(:disabled)`));
}
